import {gql} from "graphql-request";

export const GenerateDirectUpload = gql`
  mutation GenerateDirectUpload($input: GenerateDirectUploadInput!) {
    generateDirectUpload(input: $input) {
      uploadUrl
      headers
      signedId
    }
  }
`;

export const SearchUsersRecordedTrails = gql`
  query SearchUsersRecordedTrails(
    $string: String!
    $page: Int
    $pageSize: Int
    $startDate: ISO8601DateTime
    $endDate: ISO8601DateTime
    $sort: String
  ) {
    recordingsTextSearch(
      string: $string
      page: $page
      pageSize: $pageSize
      startDate: $startDate
      endDate: $endDate
      sort: $sort
    ) {
      id
      title
      distanceMeters
      thumbnailUrl
      obfuscatedSlug
      createdAt
      shareableUrl
      elapsedSeconds
      elevationGainMeters
      lineString
      timeStamps
      public
      user {
        id
      }
      images {
        file {
          url
          smUrl
          mdUrl
          lgUrl
          processed
        }
      }
      trail {
        id
        name
        trailDistance
        durationString
        shortAddress
        staticMapImage
        jumboImage {
          id
          url
          metadata
        }
        slug
        regionSlug
        countrySlug
      }
      connectedHike {
        id
        name
        shareableUrl
      }
    }
  }
`;

export const SearchUsersRecordedTrailsPage = gql`
  query SearchUsersRecordedTrailsPage(
    $string: String!
    $page: Int
    $pageSize: Int
    $startDate: ISO8601DateTime
    $endDate: ISO8601DateTime
  ) {
    recordingsTextSearch(
      string: $string
      page: $page
      pageSize: $pageSize
      startDate: $startDate
      endDate: $endDate
    ) {
      id
      title
      distanceMeters
      thumbnailUrl
      obfuscatedSlug
      createdAt
      shareableUrl
      elapsedSeconds
      elevationGainMeters
      timeStamps
      public
      user {
        id
      }
      images {
        file {
          url
          smUrl
          mdUrl
          processed
        }
      }
      trail {
        id
        name
        trailDistance
        durationString
        shortAddress
        staticMapImage
        jumboImage {
          id
          url
          metadata
        }
        slug
        regionSlug
        countrySlug
      }
      connectedHike {
        id
        name
        shareableUrl
      }
    }
  }
`;

export const GetRecordedTrailById = gql`
  query GetRecordedTrailById($slug: String!) {
    recordingBySlug(slug: $slug) {
      id
      clientId
      updatedAt
      public
      createdAt
      lineString
      body
      title
      deleted
      obfuscatedSlug
      images {
        id
        firebaseImageReference
        clientId
        file {
          url
        }
      }
      startAddress {
        region
        regionCountry
        city
        cityDistrict
        country
        name
      }
      elapsedSeconds
      distanceMeters
      elevationGainMeters
      errors {
        fieldName
        errors
      }
      timeStamps
      verticalAccuracy
      horizontalAccuracy
      course
      courseAccuracy
      speed
      thumbnailUrl
      speedAccuracy
      shareableUrl
      altitudes
      distances
      recording {
        id
      }
      plannedTrail {
        id
      }
      connectedHike {
        id
        name
        shareableUrl
        startAddress {
          cityCountry
        }
      }
      publicUser {
        displayName
        firebaseUserId
      }
    }
  }
`;

export const GetRecordedTrailByRecordingId = gql`
  query GetRecordedTrailById($id: ID!) {
    recording(id: $id) {
      id
      clientId
      updatedAt
      public
      createdAt
      lineString
      body
      title
      deleted
      obfuscatedSlug
      images {
        id
        file {
          url
        }
      }
      startAddress {
        region
        regionCountry
        city
        cityDistrict
        country
        name
      }
      elapsedSeconds
      distanceMeters
      elevationGainMeters
      errors {
        fieldName
        errors
      }
      timeStamps
      verticalAccuracy
      horizontalAccuracy
      course
      courseAccuracy
      speed
      thumbnailUrl
      speedAccuracy
      shareableUrl
      altitudes
      distances
      recording {
        id
      }
      plannedTrail {
        id
      }
      connectedHike {
        id
        name
        shareableUrl
        startAddress {
          cityCountry
        }
      }
      publicUser {
        displayName
        firebaseUserId
      }
    }
  }
`;

// The iframe embed's own query — deliberately lean. GetRecordedTrailById pulls
// images, addresses and per-sample accuracy arrays the embed never draws, and an
// embed is served to anonymous visitors on someone else's page.
export const GetRecordingEmbed = gql`
  query GetRecordingEmbed($slug: String!) {
    recordingBySlug(slug: $slug) {
      id
      title
      lineString
      distanceMeters
      elevationGainMeters
      elapsedSeconds
      shareableUrl
    }
  }
`;

export const GetRecordedTrailLineStringById = gql`
  query GetRecordedTrailById($input: ID!) {
    recording(id: $input) {
      id
      title
      lineString
      # The owner's bagged summits, celebrated by the 3D preview (flyoverPeakOverlay.ts).
      flyoverPeaks {
        id
        name
        lng
        lat
        elevationMeters
        firstBag
        listName
        listBaggedCount
        listPeaksCount
        badgeUrl
        accent
      }
    }
  }
`;

export const GetRecordingExplore = gql`
  query GetRecordingExplore($id: ID!) {
    recording(id: $id) {
      id
      title
      obfuscatedSlug
      lineString
      elapsedSeconds
      elevationGainMeters
      distanceMeters
      thumbnailUrl
      createdAt
      shareableUrl
      startAddress {
        city
      }
      publicUser {
        firebaseUserId
      }
      timeStamps
    }
  }
`;

export const GetRecordingEncodedPolyline = gql`
  query GetRecordingEncodedPolyline($id: ID!) {
    recording(id: $id) {
      id
      encodedPolyline
    }
  }
`;

export const GetRecordingGpxById = gql`
  query GetRecordingGpxById($id: ID!) {
    recordingGpx(id: $id)
  }
`;

export const CreateRecording = gql`
  mutation CreateRecording($input: CreateRecordingInput!) {
    createRecording(input: $input) {
      id
      title
      shareableUrl
    }
  }
`;

export const DestroyRecording = gql`
  mutation DestroyRecording($input: DestroyRecordingInput!) {
    destroyRecording(input: $input) {
      id
      title
      createdAt
      deleted
    }
  }
`;

// The Recordings bin. Deletes are soft and kept for
// Recording::PURGE_RETENTION_DAYS, and `searchUserRecordings(deleted: true)` returns
// exactly the ones still inside that window, newest deletion first — moderation
// takedowns and already-expired rows are excluded server-side, so everything listed here
// is genuinely restorable.
export const SearchDeletedUserRecordings = gql`
  query SearchDeletedUserRecordings($page: Int, $perPage: Int) {
    searchUserRecordings(deleted: true, page: $page, perPage: $perPage) {
      id
      title
      createdAt
      deletedAt
      restorable
      distanceMeters
      elapsedSeconds
      elevationGainMeters
      thumbnailUrl
      obfuscatedSlug
    }
  }
`;

export const RestoreRecording = gql`
  mutation RestoreRecording($id: ID!) {
    restoreRecording(input: { id: $id }) {
      restored
      recording {
        id
        title
        deleted
        deletedAt
      }
      errors {
        fieldName
        errors
      }
    }
  }
`;

export const UserRecordingsQuery = gql`
  query UserRecordings($page: Int) {
    recordings(page: $page) {
      id
      title
      createdAt
      thumbnailUrl
      shareableUrl
      distanceMeters
      elapsedSeconds
      elevationGainMeters
      public
      trail {
        id
        name
        trailDistance
        durationString
        shortAddress
        staticMapImage
        jumboImage {
          id
          url
          metadata
        }
        regionSlug
        countrySlug
        slug
      }
    }
  }
`;

export const UpdateRecording = gql`
  mutation UpdateRecording($input: UpdateRecordingInput!) {
    updateRecording(input: $input) {
      id
      title
      body
      createdAt
    }
  }
`;

export const SearchUserRecordingsSimple = gql`
  query SearchUserRecordingsSimple(
    $query: String
    $page: Int
    $perPage: Int
  ) {
    searchUserRecordings(
      query: $query
      page: $page
      perPage: $perPage
    ) {
      id
      title
      obfuscatedSlug
      distanceMeters
      thumbnailUrl
      shareableUrl
      createdAt
      public
      connectedHike {
        id
        name
        shareableUrl
      }
    }
  }
`;

export const SearchUserRecordingsCount = gql`
  query SearchUserRecordingsCount($query: String) {
    searchUserRecordingsCount(query: $query)
  }
`;

export const GenerateFlyoverVideo = gql`
  mutation GenerateFlyoverVideo($input: GenerateFlyoverVideoInput!) {
    generateFlyoverVideo(input: $input) {
      id
      status
      progress
      url
      aspect
      speed
      mapStyleUrl
      errorMessage
    }
  }
`;

export const FlyoverVideosForRecording = gql`
  query FlyoverVideosForRecording($id: ID!) {
    recording(id: $id) {
      id
      updatedAt
      flyoverVideos {
        id
        hideLogo
        hideStats
        skipIntro
        status
        progress
        url
        aspect
        mapStyleUrl
        renderedAt
        errorMessage
      }
    }
  }
`;
